import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye, Ear } from 'lucide-react';
export const InteractionSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const [activeIndex, setActiveIndex] = useState(0);
  const interactiveExperiences = [{
    title: 'Sensory Battlefield',
    description: "Experience the sights, sounds, and scents of the Sikh army's legendary presence through interactive technology.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7baikykwbzRgddsVg9e56P/Gemini_Generated_Image_k59hd9k59hd9k59h.png',
    icon: <div className="w-6 h-6" data-id="element-1614" data-mp="28so6nde5z7dz" />,
    features: ['Authentic scent triggers', 'Responsive floor projections', '360° spatial audio']
  }, {
    title: 'Soaring Perspectives',
    description: "Witness the Sikh Empire's vast armies through the eyes of a soaring eagle.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kNtxVUY15aZ1oUPHDuoV4h/Gemini_Generated_Image_odykz3odykz3odyk.png',
    icon: <Eye className="w-6 h-6" data-id="element-1615" data-mp="3ewfrx8aaylyi" />,
    features: ['Dynamic flight perspective', 'Movement-responsive environment', 'Cinematic score']
  }, {
    title: 'Royal Court Audience',
    description: 'Stand before Maharaja Ranjit Singh in an interactive audience with voice-activated responses.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6b4EV17tqfo8Fc5y2CvfMw/Gemini_Generated_Image_8bwach8bwach8bwa.png',
    icon: <Ear className="w-6 h-6" data-id="element-1616" data-mp="28abt7h8ub2jp" />,
    features: ['Voice recognition technology', 'Position-tracking cameras', 'Authentic court protocols']
  }];
  const nextSlide = () => {
    setActiveIndex(prev => prev === interactiveExperiences.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setActiveIndex(prev => prev === 0 ? interactiveExperiences.length - 1 : prev - 1);
  };
  const goToSlide = (index: number) => {
    setActiveIndex(index);
  };
  return <motion.section id="interaction" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1617" data-mp="m2ii2ocse1jj">
      <div className="container mx-auto px-4" data-id="element-1618" data-mp="v3oy6wbixac9">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-24" data-id="element-1619" data-mp="zw9uc4hwjziy">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1620" data-mp="1dwavll22x8yy">
            Interactive Technology
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1621" data-mp="2uc85wgdj6kzn">
            BECOME PART
            <br data-id="element-1622" data-mp="2pd3ztylwl3dj" />
            <span className="text-amber-400" data-id="element-1623" data-mp="2pclrw4pvp0ps">OF HISTORY</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1624" data-mp="2ke0ro5ammh1c"></div>
        </motion.div>
        <div className="max-w-7xl mx-auto" data-id="element-1625" data-mp="2kg52l76y0gb4">
          <div className="relative" data-id="element-1626" data-mp="3i22jj05ocdky">
            <AnimatePresence mode="wait" data-id="element-1627" data-mp="3i0zruq0j273z">
              <motion.div key={activeIndex} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-1628" data-mp="3d2ybwdyeenwq">
                <div className="border border-white/10 rounded-lg overflow-hidden" data-id="element-1629" data-mp="3w1kb1nacu4y0">
                  <div className="relative" data-id="element-1630" data-mp="3vzheslilzfku">
                    <div className="aspect-[21/9]" data-id="element-1631" data-mp="3gjawiqu24e32">
                      <img src={interactiveExperiences[activeIndex].image} alt={interactiveExperiences[activeIndex].title} className="w-full h-full object-cover" data-id="element-1632" data-mp="3glnayu0lw6bd" />
                    </div>
                    <div className="absolute top-4 right-4 opacity-50" data-id="element-1633" data-mp="3bkpy8wyd84h9">
                      <span className="text-xs text-white/80 font-mono tracking-wider" data-id="element-1634" data-mp="35pfitl0m867">
                        ATHINK STUDIO
                      </span>
                    </div>
                  </div>
                  <div className="p-8 bg-black/80 border-t border-white/10" data-id="element-1635" data-mp="3pjfts85jdw3i">
                    <div className="flex items-center mb-4" data-id="element-1636" data-mp="3uik26wkmzpss">
                      <div className="text-amber-400 mr-3" data-id="element-1637" data-mp="3ugz6v4qi02tv">
                        {interactiveExperiences[activeIndex].icon}
                      </div>
                      <h3 className="font-serif text-3xl text-white" data-id="element-1638" data-mp="qva7g1z5cfrm">
                        {interactiveExperiences[activeIndex].title}
                      </h3>
                    </div>
                    <p className="text-white/70 text-lg mb-6 font-light" data-id="element-1639" data-mp="26khx1mfv6wd4">
                      {interactiveExperiences[activeIndex].description}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1640" data-mp="2gbmb8jxpud6w">
                      {interactiveExperiences[activeIndex].features.map((feature, idx) => <motion.div key={idx} initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: idx * 0.1 + 0.3
                    }} className="border-l border-amber-400/30 pl-4" data-id="element-1641" data-mp="1nmykw9y40xmh" data-mpi={idx}>
                            <p className="text-white/60 text-sm font-light" data-id="element-1642" data-mp="2anyu9z9sm01u">
                              {feature}
                            </p>
                          </motion.div>)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <button onClick={prevSlide} className="absolute left-4 top-[20%] transform -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10 z-10" aria-label="Previous slide" data-id="element-1643" data-mp="1n0h76h1h3q57">
              <ChevronLeft className="w-6 h-6" data-id="element-1644" data-mp="20s466omsvcro" />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-[20%] transform -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10 z-10" aria-label="Next slide" data-id="element-1645" data-mp="139vqfazo2bjo">
              <ChevronRight className="w-6 h-6" data-id="element-1646" data-mp="vrnrpl49kxbt" />
            </button>
          </div>
          <div className="flex justify-center mt-8 space-x-2" data-id="element-1647" data-mp="qqqb3cywkbcf">
            {interactiveExperiences.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-px transition-all duration-300 ${index === activeIndex ? 'w-12 bg-amber-400' : 'w-8 bg-white/30 hover:bg-white/50'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1648" data-mp="mq6hgjdjfpv1" data-mpi={index} />)}
          </div>
        </div>
      </div>
    </motion.section>;
};